/**
 * useAgreements Hook
 * Manages all agreements-related state and operations
 */

import { useState, useMemo, useRef } from 'react';
import { AxiosError } from 'axios';
import { apiClient } from '@/shared/api';
import { useFetch, useTableData } from '@/hooks';
import { handleApiError, showSuccess, showError } from '@/utils/errorHandler';
import { API_BASE_URL } from '@/config/api';
import type { Agreement } from '@/types/admin';

// Constants
const RECENT_TEMPLATES_KEY = 'recentPdfTemplates';
const MAX_RECENT_TEMPLATES = 10;

export interface PdfTemplate {
    filename: string;
    name: string;
    size: number;
    modified_at: string;
    fillable: boolean;
}

export interface AgreementPropertyContext {
    propertyId?: number;
    propertyAddress?: string;
}

export function useAgreements() {
    // UI State
    const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState<number | null>(null);

    // PDF Editor state
    const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Recent templates tracking
    const [recentFilenames, setRecentFilenames] = useState<string[]>(() => {
        try {
            const stored = localStorage.getItem(RECENT_TEMPLATES_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });

    // Fetch agreements
    const {
        data: agreements = [],
        isLoading: agreementsLoading,
        refetch: refetchAgreements,
        setData: setAgreements
    } = useFetch<Agreement[]>(
        () => apiClient.get('/agreements/list').then(res => {
            if (res.data?.success && Array.isArray(res.data?.data)) {
                return res.data.data.map((a: Record<string, unknown>) => ({
                    id: a.id,
                    template_name: a.template_name,
                    customer_name: a.customer_name,
                    customer_email: a.customer_email,
                    customer_phone: a.customer_phone,
                    status: a.status,
                    pdf_path: a.pdf_path,
                    property_id: a.property_id,
                    property_address: a.property_address,
                    created_at: a.created_at,
                    updated_at: a.updated_at,
                    approval_email_sent_at: a.approval_email_sent_at,
                    approval_viewed_at: a.approval_viewed_at,
                    approved_at: a.approved_at,
                    dotloop_loop_id: a.dotloop_loop_id,
                    dotloop_profile_id: a.dotloop_profile_id,
                    dotloop_document_id: a.dotloop_document_id,
                    dotloop_loop_url: a.dotloop_loop_url,
                    dotloop_sent_at: a.dotloop_sent_at,
                }));
            }
            return [];
        }),
        { immediate: true, actionName: 'load agreements' }
    );

    // Fetch PDF templates
    const {
        data: pdfTemplates = [],
        isLoading: templatesLoading,
        refetch: refetchTemplates
    } = useFetch<PdfTemplate[]>(
        () => apiClient.get('/agreements/pdf-templates').then(res => {
            if (res.data?.success && Array.isArray(res.data?.data)) {
                return res.data.data;
            }
            return [];
        }),
        { immediate: true, actionName: 'load PDF templates' }
    );

    // Only fillable templates for Create New tab
    const fillableTemplates = useMemo(
        () => pdfTemplates.filter(t => t.fillable),
        [pdfTemplates]
    );

    // Table data
    const tableData = useTableData(agreements, {
        searchFields: ['customer_name', 'customer_email', 'template_name'],
        defaultSort: { field: 'created_at', direction: 'desc' },
        perPage: 10,
    });

    // Track template usage
    const trackTemplateUsage = (filename: string) => {
        const updated = [filename, ...recentFilenames.filter(f => f !== filename)].slice(0, MAX_RECENT_TEMPLATES);
        setRecentFilenames(updated);
        localStorage.setItem(RECENT_TEMPLATES_KEY, JSON.stringify(updated));
    };

    // Select template
    const handleSelectTemplate = (template: PdfTemplate) => {
        trackTemplateUsage(template.filename);
        setSelectedTemplate(template);
    };

    // Upload template
    const handleUploadTemplate = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.pdf')) {
            showError('Please select a PDF file');
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            await apiClient.post('/agreements/pdf-templates/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            showSuccess(`Template "${file.name}" uploaded successfully`);
            refetchTemplates();
        } catch (error) {
            handleApiError(error, 'upload template');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Create approval link (without sending email)
    const handleCreateApprovalLink = async (agreement: Agreement): Promise<string | null> => {
        setActionLoading(agreement.id);
        try {
            const response = await apiClient.post(`/agreements/${agreement.id}/create-approval-link`, {
                send_email: false
            });
            if (response.data?.success) {
                refetchAgreements();
                return response.data.data?.approval_link || null;
            }
            return null;
        } catch (error) {
            handleApiError(error, 'create approval link');
            return null;
        } finally {
            setActionLoading(null);
        }
    };

    // Send approval email for an existing link
    const handleSendApprovalEmail = async (agreement: Agreement) => {
        setActionLoading(agreement.id);
        try {
            const response = await apiClient.post(`/agreements/${agreement.id}/create-approval-link`, {
                send_email: true
            });
            if (response.data?.success) {
                if (response.data?.data?.email_blacklisted) {
                    showError(`Email not sent: ${response.data.data.blacklist_reason}`);
                } else if (response.data?.data?.email_sent) {
                    showSuccess('Approval email sent successfully');
                } else {
                    showSuccess('Approval link created');
                }
                refetchAgreements();
            }
        } catch (error) {
            handleApiError(error, 'send approval email');
        } finally {
            setActionLoading(null);
        }
    };

    const handleAssignPropertyLoop = async (
        agreement: Agreement,
        propertyContext: AgreementPropertyContext
    ): Promise<boolean> => {
        setActionLoading(agreement.id);
        try {
            const response = await apiClient.post(`/agreements/${agreement.id}/assign-property-loop`, {
                property_id: propertyContext.propertyId,
                property_address: propertyContext.propertyAddress,
            });
            if (response.data?.success) {
                showSuccess(
                    response.data?.data?.created_new_loop
                        ? 'Property assigned and Dotloop loop created'
                        : 'Property assigned and Dotloop loop updated'
                );
                refetchAgreements();
                return true;
            }
            return false;
        } catch (error) {
            handleApiError(error, 'assign property');
            return false;
        } finally {
            setActionLoading(null);
        }
    };

    // View PDF - uses short-lived single-use token (no auth token in URL)
    const handleViewPdf = async (agreement: Agreement) => {
        const newWindow = window.open('', '_blank');
        setActionLoading(agreement.id);
        try {
            const response = await apiClient.post(`/agreements/${agreement.id}/view-token`);
            const viewToken = response.data?.data?.view_token;
            if (viewToken && newWindow) {
                newWindow.location.href = `${API_BASE_URL}/agreements/view/${viewToken}`;
            } else {
                newWindow?.close();
                showError('Failed to generate view link');
            }
        } catch (error) {
            newWindow?.close();
            handleApiError(error, 'view PDF');
        } finally {
            setActionLoading(null);
        }
    };

    // Download PDF - uses short-lived single-use token with download flag
    const handleDownloadPdf = async (agreement: Agreement) => {
        const newWindow = window.open('', '_blank');
        setActionLoading(agreement.id);
        try {
            const response = await apiClient.post(`/agreements/${agreement.id}/view-token`);
            const viewToken = response.data?.data?.view_token;
            if (viewToken && newWindow) {
                newWindow.location.href = `${API_BASE_URL}/agreements/view/${viewToken}?download=1`;
            } else {
                newWindow?.close();
                showError('Failed to generate download link');
            }
        } catch (error) {
            newWindow?.close();
            handleApiError(error, 'download PDF');
        } finally {
            setActionLoading(null);
        }
    };

    // Update status
    const handleUpdateStatus = async (agreement: Agreement, newStatus: string) => {
        setActionLoading(agreement.id);
        try {
            await apiClient.patch(`/agreements/${agreement.id}/status`, { status: newStatus });
            showSuccess(`Status updated to ${newStatus}`);
            refetchAgreements();
        } catch (error) {
            handleApiError(error, 'update status');
        } finally {
            setActionLoading(null);
        }
    };

    // Delete agreement
    const handleDeleteAgreement = async () => {
        if (!selectedAgreement) return;
        const agreementId = selectedAgreement.id;
        setActionLoading(selectedAgreement.id);
        try {
            await apiClient.delete(`/agreements/${agreementId}`);
            setAgreements(current => current?.filter(agreement => agreement.id !== agreementId) ?? []);
            showSuccess('Agreement deleted');
            setDeleteDialogOpen(false);
            setSelectedAgreement(null);
            refetchAgreements();
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 404) {
                setAgreements(current => current?.filter(agreement => agreement.id !== agreementId) ?? []);
                showSuccess('Agreement already deleted');
                setDeleteDialogOpen(false);
                setSelectedAgreement(null);
                refetchAgreements();
            } else {
                handleApiError(error, 'delete agreement');
            }
        } finally {
            setActionLoading(null);
        }
    };

    // Save agreement
    const handleSaveAgreement = async (
        pdfBase64: string,
        customerInfo: { name: string; email: string; phone: string },
        propertyContext?: AgreementPropertyContext,
        onSuccess?: () => void
    ) => {
        if (!selectedTemplate) return;

        setIsSaving(true);
        try {
            const response = await apiClient.post('/agreements/save', {
                templateName: selectedTemplate.name,
                customerName: customerInfo.name,
                customerEmail: customerInfo.email,
                customerPhone: customerInfo.phone,
                pdfBase64,
                propertyId: propertyContext?.propertyId,
                propertyAddress: propertyContext?.propertyAddress,
            });
            if (response.data?.data?.dotloop_sent) {
                showSuccess('Agreement saved and uploaded to Dotloop');
            } else if (response.data?.data?.dotloop_error) {
                showError(`Agreement saved but Dotloop upload failed: ${response.data.data.dotloop_error}`);
            } else {
                showSuccess('Agreement saved successfully');
            }
            setSelectedTemplate(null);
            refetchAgreements();
            onSuccess?.();
        } catch (error) {
            handleApiError(error, 'save agreement');
        } finally {
            setIsSaving(false);
        }
    };

    // Fill & preview PDF
    const handleFillPreview = async (
        templateFilename: string,
        contactInfo: { name: string; email: string; phone: string; contactId?: number },
        options: {
            preferred_locations?: string;
            agreement_duration_months?: number;
            commission_percent?: string;
            protection_period_days?: string;
            agency_type?: string;
        } = {}
    ): Promise<{ pdfBase64: string; customerName: string; customerEmail: string; customerPhone: string } | null> => {
        try {
            const response = await apiClient.post('/agreements/fill-preview', {
                template_filename: templateFilename,
                customer_name: contactInfo.name,
                customer_email: contactInfo.email,
                customer_phone: contactInfo.phone,
                contact_id: contactInfo.contactId,
                preferred_locations: options.preferred_locations || '',
                agreement_duration_months: options.agreement_duration_months || 6,
                commission_percent: options.commission_percent || '2.5',
                protection_period_days: options.protection_period_days || '90',
                agency_type: options.agency_type || 'dual',
            });

            if (response.data?.success) {
                return response.data.data;
            }
            showError('Failed to generate PDF preview');
            return null;
        } catch (error) {
            handleApiError(error, 'generate PDF preview');
            return null;
        }
    };

    // Save & Send (save + create approval link + email)
    const handleSaveAndSend = async (
        pdfBase64: string,
        customerInfo: { name: string; email: string; phone: string },
        propertyContext?: AgreementPropertyContext,
        onSuccess?: () => void
    ) => {
        if (!selectedTemplate) return;

        setIsSaving(true);
        try {
            // Step 1: Save agreement
            const saveResponse = await apiClient.post('/agreements/save', {
                templateName: selectedTemplate.name,
                customerName: customerInfo.name,
                customerEmail: customerInfo.email,
                customerPhone: customerInfo.phone,
                pdfBase64,
                propertyId: propertyContext?.propertyId,
                propertyAddress: propertyContext?.propertyAddress,
            });

            if (!saveResponse.data?.success) {
                showError('Failed to save agreement');
                return;
            }

            const agreementId = saveResponse.data.data.id;

            // Step 2: Create approval link + send email
            const approvalResponse = await apiClient.post(`/agreements/${agreementId}/create-approval-link`, {
                send_email: true,
            });

            if (approvalResponse.data?.success) {
                if (approvalResponse.data?.data?.email_blacklisted) {
                    showError(`Saved but email not sent: ${approvalResponse.data.data.blacklist_reason}`);
                } else if (approvalResponse.data?.data?.email_sent && saveResponse.data?.data?.dotloop_sent) {
                    showSuccess('Agreement uploaded to Dotloop and email sent');
                } else if (approvalResponse.data?.data?.email_sent) {
                    showSuccess('Agreement saved and email sent');
                } else {
                    showSuccess('Agreement saved, approval link created');
                }
            }

            setSelectedTemplate(null);
            refetchAgreements();
            onSuccess?.();
        } catch (error) {
            handleApiError(error, 'save and send agreement');
        } finally {
            setIsSaving(false);
        }
    };

    // Get PDF URL
    const getPdfUrl = (filename: string) => {
        return `${API_BASE_URL}/agreements/pdf-templates/${encodeURIComponent(filename)}`;
    };

    // Recent templates (only fillable ones)
    const recentTemplates = useMemo(() => {
        return recentFilenames
            .map(filename => fillableTemplates.find(t => t.filename === filename))
            .filter((t): t is PdfTemplate => t !== undefined)
            .slice(0, 5);
    }, [recentFilenames, fillableTemplates]);

    // Open delete dialog
    const openDeleteDialog = (agreement: Agreement) => {
        setSelectedAgreement(agreement);
        setDeleteDialogOpen(true);
    };

    // Close delete dialog
    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setSelectedAgreement(null);
    };

    return {
        // Data
        agreements,
        pdfTemplates,
        fillableTemplates,
        recentTemplates,
        selectedTemplate,
        selectedAgreement,

        // Loading states
        agreementsLoading,
        templatesLoading,
        actionLoading,
        isSaving,
        isUploading,

        // Dialog state
        deleteDialogOpen,
        openDeleteDialog,
        closeDeleteDialog,

        // Table data
        tableData,

        // Refs
        fileInputRef,

        // Handlers
        handleSelectTemplate,
        handleUploadTemplate,
        handleCreateApprovalLink,
        handleSendApprovalEmail,
        handleAssignPropertyLoop,
        handleViewPdf,
        handleDownloadPdf,
        handleUpdateStatus,
        handleDeleteAgreement,
        handleSaveAgreement,
        handleFillPreview,
        handleSaveAndSend,
        getPdfUrl,
        clearSelectedTemplate: () => setSelectedTemplate(null),
    };
}

export type UseAgreementsReturn = ReturnType<typeof useAgreements>;
